// =====================================================================================
// INVRT Radar - web console - the delivery workspace hooks (story H07)
//
// The four HIGH-INTENT organisation delivery routes on react-query, with the tenant
// hygiene the UI law demands: every query key carries the effective USER id, the
// ORGANISATION id and the campaign key, so an organisation switch refetches instead of
// rendering another tenant's data for a frame, and the previous tenant's rows are gone
// the moment the switch happens. Drafts live in sessionStorage under an
// organisation+campaign key: a refresh keeps the user's edits, a switch cannot see
// another tenant's draft.
//
// SAVE IS NOT SEND. The save is a PUT of the selection document with the engine's CAS
// expected_revision. It persists a draft selection and returns the readiness block; it
// never dispatches, contacts a destination or implies sending. A 409
// selection_revision_conflict is surfaced with the canonical revision and the user's
// draft is preserved for the re-read.
// =====================================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { isApiError } from "@/lib/api";
import {
  clearDeliveryDraft,
  fetchDeliveryPreview,
  fetchDeliveryReadiness,
  fetchDeliverySelection,
  hiDeliveryConfigError,
  hiDeliveryOrigin,
  loadDeliveryDraft,
  saveDeliverySelection,
  storeDeliveryDraft,
  type DeliveryDraft,
  type DeliveryPreview,
  type DeliveryReadiness,
  type DeliverySaveResult,
  type DeliverySelection,
  type DeliverySelectionState,
  type HiDeliveryContext,
} from "@/lib/hiDelivery";
import { useSession } from "@/auth/SessionProvider";

export function selectionKey(userId: string | null, orgId: string | null, campaignKey: string): readonly unknown[] {
  return ["hi-delivery", "selection", userId, orgId, campaignKey];
}

export function readinessKey(userId: string | null, orgId: string | null, campaignKey: string): readonly unknown[] {
  return ["hi-delivery", "readiness", userId, orgId, campaignKey];
}

/** The conflict the CAS save can answer, with everything the UI needs to act. */
export interface DeliveryConflict {
  token: string;
  /** The canonical current revision the engine returned, when it did. */
  currentRevision: number | null;
  correlationId: string | null;
}

export interface DeliveryWorkspace {
  selection: UseQueryResult<DeliverySelectionState>;
  readiness: UseQueryResult<DeliveryReadiness>;
  preview: UseQueryResult<DeliveryPreview>;
  /** The stored draft, or null. Kept across a refresh, scoped to one org+campaign. */
  draft: DeliveryDraft | null;
  hasDraft: boolean;
  /** Starts (or updates) the draft from the revision currently known. */
  setDraft: (selection: DeliverySelection) => void;
  clearDraft: () => void;
  /** PUT the selection with CAS. Draft persistence only - never a send. With an argument,
   * persists that exact selection; without, the stored draft. */
  save: (value?: DeliverySelection) => void;
  savePending: boolean;
  saveError: unknown;
  /** The last successful save's answer (new revision + readiness), or null. */
  saveResult: DeliverySaveResult | null;
  /** Non-null while a version conflict is the latest save outcome. */
  conflict: DeliveryConflict | null;
  dismissConflict: () => void;
  uncertainSave: boolean;
  reconciliation: "matching" | "retry_allowed" | "conflict" | null;
  reconciling: boolean;
  reconcileSave: () => Promise<void>;

}

export function useDeliveryWorkspace(campaignKey: string): DeliveryWorkspace {
  const { session, orgId } = useSession();
  const cache = useQueryClient();
  const userId = session?.user.id ?? null;
  const accessToken = session?.access_token ?? null;

  const origin = hiDeliveryOrigin();
  const ctx = useMemo<HiDeliveryContext>(
    () => ({ origin: origin ?? "", accessToken }),
    [accessToken, origin],
  );

  const selection = useQuery({
    queryKey: selectionKey(userId, orgId, campaignKey),
    enabled: orgId !== null && userId !== null && campaignKey !== "",
    queryFn: async ({ signal }) => {
      if (origin === null) throw hiDeliveryConfigError();
      return fetchDeliverySelection({ ...ctx, signal }, campaignKey, orgId ?? "");
    },
    retry: false,
    staleTime: 15_000,
  });

  const readiness = useQuery({
    queryKey: readinessKey(userId, orgId, campaignKey),
    enabled: orgId !== null && userId !== null && campaignKey !== "",
    queryFn: async ({ signal }) => {
      if (origin === null) throw hiDeliveryConfigError();
      return fetchDeliveryReadiness({ ...ctx, signal }, campaignKey, orgId ?? "");
    },
    retry: false,
    staleTime: 15_000,
  });

  const scope = JSON.stringify([userId, orgId, campaignKey]);
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const [storedDraft, setStoredDraft] = useState<{ scope: string; value: DeliveryDraft | null }>({ scope, value: null });
  const draft = storedDraft.scope === scope ? storedDraft.value : null;
  const [checkingScope, setCheckingScope] = useState<string | null>(null);
  const pendingSave = useRef(false);

  useEffect(() => {
    setStoredDraft({ scope, value: orgId === null || campaignKey === "" ? null : loadDeliveryDraft(orgId, campaignKey) });
  }, [scope, orgId, campaignKey]);

  const persist = useCallback((value: DeliveryDraft | null) => {
    if (orgId === null || campaignKey === "") return;
    if (value === null) clearDeliveryDraft(orgId, campaignKey);
    else storeDeliveryDraft(orgId, campaignKey, value);
    setStoredDraft({ scope, value });
  }, [orgId, campaignKey, scope]);

  const setDraft = useCallback((value: DeliverySelection) => {
    if (pendingSave.current || (draft?.attempt && draft.attempt.status !== "conflict")) return;
    const cached = cache.getQueryData<DeliverySelectionState>(selectionKey(userId, orgId, campaignKey));
    persist({ selection: value, basedOnRevision: cached?.revision ?? selection.data?.revision ?? 0, savedAt: new Date().toISOString() });
  }, [cache, userId, orgId, campaignKey, draft, selection.data?.revision, persist]);

  type SaveInput = {
    scope: string; orgId: string; campaignKey: string; userId: string | null;
    ctx: HiDeliveryContext; draft: DeliveryDraft;
  };
  const saveMutation = useMutation({
    mutationFn: (input: SaveInput) => saveDeliverySelection(input.ctx, input.campaignKey, input.orgId,
      input.draft.basedOnRevision, input.draft.selection),
    onSuccess: (_answer, input) => {
      clearDeliveryDraft(input.orgId, input.campaignKey);
      if (activeScope.current === input.scope) setStoredDraft({ scope: input.scope, value: null });
      void cache.invalidateQueries({ queryKey: selectionKey(input.userId, input.orgId, input.campaignKey) });
      void cache.invalidateQueries({ queryKey: readinessKey(input.userId, input.orgId, input.campaignKey) });
    },
    onError: (error, input) => {
      const uncertain = isApiError(error) && error.token === "delivery_save_unknown";
      const next: DeliveryDraft = {
        selection: input.draft.selection, basedOnRevision: input.draft.basedOnRevision, savedAt: input.draft.savedAt,
        ...(uncertain ? { attempt: { status: "unknown" as const, correlationId: error.correlationId } } : {}),
      };
      storeDeliveryDraft(input.orgId, input.campaignKey, next);
      if (activeScope.current === input.scope) setStoredDraft({ scope: input.scope, value: next });
    },
    onSettled: () => { pendingSave.current = false; },
  });
  const currentMutation = saveMutation.variables?.scope === scope;

  const clearDraft = useCallback(() => {
    if (pendingSave.current || draft?.attempt?.status === "unknown") return;
    persist(null);
    saveMutation.reset();
  }, [draft, persist, saveMutation]);

  const save = useCallback((value?: DeliverySelection) => {
    if (pendingSave.current || orgId === null || origin === null) return;
    if (draft?.attempt && draft.attempt.status !== "retry_allowed") return;
    // After reconciliation only the original CAS document may be retried. No new
    // idempotency field exists in this frozen wire contract, so none is invented.
    const target = draft?.attempt ? draft.selection : value ?? draft?.selection ?? null;
    if (target === null) return;
    const next: DeliveryDraft = {
      selection: target, basedOnRevision: draft?.basedOnRevision ?? selection.data?.revision ?? 0,
      savedAt: draft?.savedAt ?? new Date().toISOString(),
      attempt: { status: "unknown", correlationId: draft?.attempt?.correlationId ?? null },
    };
    persist(next);
    pendingSave.current = true;
    saveMutation.mutate({ scope, orgId, campaignKey, userId, ctx, draft: next });
  }, [draft, orgId, origin, selection.data?.revision, persist, saveMutation, scope, campaignKey, userId, ctx]);

  const conflict = useMemo<DeliveryConflict | null>(() => {
    if (draft?.attempt?.status === "conflict") return {
      token: "selection_revision_conflict", currentRevision: selection.data?.revision ?? null,
      correlationId: draft.attempt.correlationId,
    };
    const error = currentMutation ? saveMutation.error : null;
    if (!isApiError(error) || error.token !== "selection_revision_conflict") return null;
    const rawRevision = error.details?.current_revision;
    return { token: error.token, currentRevision: typeof rawRevision === "number" ? rawRevision : null, correlationId: error.correlationId };
  }, [draft, selection.data?.revision, currentMutation, saveMutation.error]);

  const dismissConflict = useCallback(() => saveMutation.reset(), [saveMutation]);
  const reconcileSave = useCallback(async () => {
    if (!draft?.attempt || pendingSave.current || checkingScope !== null) return;
    const checkedScope = scope;
    setCheckingScope(checkedScope);
    try {
      const result = await selection.refetch();
      if (!result.isSuccess || activeScope.current !== checkedScope) return;
      const canonical = result.data;
      const proposed = draft.selection;
      const matches = canonical.selection?.version === proposed.version && canonical.selection.mode === proposed.mode
        && (proposed.mode !== "tags" || [...canonical.selection.tags].sort().join(",") === [...proposed.tags].sort().join(","));
      const status = canonical.revision === draft.basedOnRevision ? "retry_allowed"
        : canonical.revision > draft.basedOnRevision && matches ? "matching" : "conflict";
      persist({ ...draft, attempt: { status, correlationId: draft.attempt.correlationId ?? canonical.correlationId } });
    } finally { setCheckingScope(null); }
  }, [draft, checkingScope, scope, selection, persist]);

  // The preview evaluates the DRAFT while one exists (what this change would select) and
  // the saved selection otherwise - and only then: an unconfigured selection has nothing
  // to preview, which is the setup-required state, not an error.
  const preview = useQuery({
    queryKey: ["hi-delivery", "preview", userId, orgId, campaignKey, draft === null ? "saved" : draft.selection],
    enabled:
      orgId !== null &&
      userId !== null &&
      campaignKey !== "" &&
      (draft !== null || selection.data?.configured === true),
    queryFn: async ({ signal }) => {
      if (origin === null) throw hiDeliveryConfigError();
      return fetchDeliveryPreview({ ...ctx, signal }, campaignKey, orgId ?? "", draft === null ? null : draft.selection);
    },
    retry: false,
    placeholderData: (previous) => previous,
  });

  return {
    selection,
    readiness,
    preview,
    draft,
    hasDraft: draft !== null,
    setDraft,
    clearDraft,
    save,
    savePending: currentMutation && saveMutation.isPending,
    saveError: currentMutation ? saveMutation.error : null,
    saveResult: currentMutation ? saveMutation.data ?? null : null,
    uncertainSave: draft?.attempt !== undefined,
    reconciliation: draft?.attempt?.status === "unknown" ? null : draft?.attempt?.status ?? null,
    reconciling: checkingScope === scope,
    reconcileSave,
    conflict,
    dismissConflict,
  };
}
